import React, { useEffect, useRef, useState } from 'react'
import {
  CAlert, CButton, CCard, CCardBody, CCardFooter, CCardHeader, CCol, CRow, CSpinner,
  CTable, CTableBody, CTableDataCell, CTableHead, CTableHeaderCell, CTableRow, CTooltip,
} from '@coreui/react'
import { CChart, CChartBar } from '@coreui/react-chartjs'
import { Chart } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import { opcionesEjes } from '../utils/graficos'
import { dashboardColors } from '../theme/dashboardColors'
import WidgetsKpi from '../components/dashboard/WidgetsKpi'
import OperacionChart from '../components/dashboard/OperacionChart'
import BotonCapturaChart from '../components/dashboard/BotonCapturaChart'
import BotonInfoChart from '../components/dashboard/BotonInfoChart'
import TroniaFab from '../components/TroniaFab'

Chart.register(ChartDataLabels)

// [RGM] 2026-10-05 (pedido Rafael — "Arqueo General de RUTAS"): fila por ruta
// con el dinero del COBRADOR ASIGNADO a esa ruta (el rol del usuario no
// importa). total = base + abonos − renovaciones − gastos (calculatotalruta.cs:117).
interface ArqueoRuta {
  zona: number; rutaNombre: string; cobrador: string;
  base: number; abonos: number; renovaciones: number; gastos: number; total: number;
}
interface HomeKpi {
  bases: number; metas: number; cobradoHoy: number; prestadoHoy: number;
  gastadoHoy: number; utilidadBrutaMes: number; clientesTotales: number;
  clientesNuevos: number; clientesNuevosPeriodo: number;
  clientesRenovados: number; clientesEnRiesgo: number;
  // [RGM] 2026-10-05: arqueo por ruta + Σ (total en calle).
  rutasArqueo: ArqueoRuta[]; totalRutasCalle: number;
}
interface Graficas {
  anio: number; meses: string[]; cobrado: number[]; prestado: number[];
  gastado: number[]; utilidad: number[];
  // [RGM] 2026-10-01: series de los botones Día/Mes del chart de Operación.
  diario?: { labels: string[]; cobrado: number[]; prestado: number[]; gastado: number[] };
  rolling?: { labels: string[]; cobrado: number[]; prestado: number[]; gastado: number[] };
}
interface HomePanelCartera {
  zonaCodigo: number; zonaNombre: string; cobradorCodigo: number;
  cobradorNombre: string; cobradorNombreCompleto: string; total: number;
}
interface HomePanelDesempeno { ruta: string; nuevos: number; renovaciones: number; puntajeDesempeno: number }
interface HomePanelMetaDiaria { ruta: string; meta: number; media: number; actual: number }
interface HomePanelProgreso { mesNombre: string; esperado: number; progreso: number; porcentaje: number }
interface HomePanel {
  carteraRutas: HomePanelCartera[]; carteraTotal: number; totalCartera: number;
  desempenoRutas: HomePanelDesempeno[]; progreso: HomePanelProgreso;
  metaDiariaRutas: HomePanelMetaDiaria[];
  // [RGM] 2026-10-01: dataset MENSUAL (HomeSampleDataMetas GX) para "Progreso Meta".
  metasMesRutas: HomePanelMetaMes[];
}
interface HomePanelMetaMes {
  zonaCodigo: number; ruta: string; meta: number; media: number;
  actual: number; cumplimiento: number;
}
// Totales del footer de Operación del negocio. Diseño NUEVO [RGM] aprobado
// explícitamente por Rafael 2026-08-24 (NO es réplica GX: el home.cs del GX no
// calcula "Valor Negocio"; los totales de caja vienen de cajageneral.cs y la
// cartera de home.cs:2733 — el total combinado es decisión de producto de Rafael).
interface HomeFooterTotals {
  nuevosMes: number; renovacionesMes: number; totalCajas: number; valorNegocio: number;
}

const PAGE_SIZE = 10 // subGrid1_Rows = 10 (home.cs:2627)

// Formato "$ Z,ZZZ,ZZZ,ZZ9.99" del Total Cartera (home.cs:2733): fuente única
// utils/moneda.ts (fmtMoneda — símbolo configurado + coma de miles, punto
// decimal). [RGM] 2026-10-02 (F)
// EXCEPCIÓN documentada: fmtTotal conserva la máscara "ZZZZZZZZZ9.99" de la
// columna Total de la grilla (home.cs:3760): SIN separadores.
const fmtTotal = (n: number) => n.toFixed(2)

// Str(valor, 12, 1) trimmado (home.cs:2434): 1 decimal, sin separadores
const fmtGx = (n: number) => n.toFixed(1)

const HomePage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [kpi, setKpi] = useState<HomeKpi | null>(null)
  const [graf, setGraf] = useState<Graficas | null>(null)
  const [panel, setPanel] = useState<HomePanel | null>(null)
  const [footer, setFooter] = useState<HomeFooterTotals | null>(null)
  const [page, setPage] = useState(1)
  const [cargandoCartera, setCargandoCartera] = useState(false)
  const [error, setError] = useState('')
  // [RGM] 2026-10-01: refs de las gráficas para el botón sutil de captura
  // (icono cámara absoluto — no mueve el diseño).
  const refDesempeno = useRef<HTMLDivElement>(null)
  const refProgreso = useRef<HTMLDivElement>(null)
  const refMetaDiaria = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // [RGM] 2026-09-16 (pedido Rafael): el superadmin no usa el home de empresa
    // — siempre aterriza en su resumen (/superadmin).
    if (currentUser()?.role === 'superadmin') {
      window.location.href = '/superadmin'
      return
    }
    // [RGM] 2026-09-16 (pedido Rafael): el asistente inicial es solo para admins
    // de empresa — el superusuario (admin) nunca va al wizard.
    if (currentUser()?.hasCompletedSetup === false && currentUser()?.role !== 'superadmin') {
      window.location.href = '/parametros-iniciales'
      return
    }
    api.get<HomeKpi>(`/home/kpi?empresa=${empresa}`).then(setKpi).catch((e) => setError(e.message))
    api.get<Graficas>(`/home/graficas?empresa=${empresa}`).then(setGraf).catch(() => {})
    api.get<HomePanel>(`/home/panel?empresa=${empresa}`).then(setPanel).catch((e) => setError(e.message))
    api.get<HomeFooterTotals>(`/home/footer?empresa=${empresa}`).then(setFooter).catch(() => {})
  }, [empresa])

  useEffect(() => {
    if (!panel) return
    setCargandoCartera(true)
    api.get<HomePanel>(`/home/panel?empresa=${empresa}&page=${page}`)
      .then(setPanel)
      .catch((e) => setError(e.message))
      .finally(() => setCargandoCartera(false))
  }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.hasCompletedSetup === false && user?.role !== 'superadmin') return null
  if (error && (!kpi || !graf || !panel)) return <CAlert color="danger">{error}</CAlert>
  if (!kpi || !graf || !panel) return <div className="text-center p-5"><CSpinner /></div>

  const mes = panel.progreso.mesNombre
  const totalPaginas = Math.max(1, Math.ceil(panel.carteraTotal / PAGE_SIZE))

  const desempenoLabels = panel.desempenoRutas.map((r) => r.ruta)
  const desempenoNuevos = panel.desempenoRutas.map((r) => r.nuevos)
  const desempenoRenovaciones = panel.desempenoRutas.map((r) => r.renovaciones)
  const desempenoPuntaje = panel.desempenoRutas.map((r) => r.puntajeDesempeno)
  const metaDiariaLabels = panel.metaDiariaRutas.map((r) => r.ruta)
  const metaDiariaMetas = panel.metaDiariaRutas.map((r) => r.meta)
  const metaDiariaActuales = panel.metaDiariaRutas.map((r) => r.actual)
  // Eje X del chart horizontal: hasta 1.15× la mayor meta diaria.
  const metaDiariaMax = metaDiariaMetas.reduce((max, v) => Math.max(max, v), 0)

  // [RGM] 2026-10-01 (pedido Rafael — fiel a HomeSampleDataMetas GX): datos de
  // "PROGRESO META" = Meta Mes (riel) + Progreso (llena, semáforo) + Meta Mínima
  // (línea punteada con punto por ruta). Eje Y hasta 1.15× la mayor meta.
  const metasMes = panel.metasMesRutas ?? []
  const metasMesMax = metasMes.reduce((max, m) => Math.max(max, m.meta), 0)

  return (
    <>
      <WidgetsKpi kpi={kpi} graf={graf} />
      <OperacionChart kpi={kpi} graf={graf} footer={footer} />
      <CRow className="g-4">
        {/* Desempeño productivo por ruta — título runtime home.cs:2432, chart Column 310px (home.cs:3681-3683), SDT Ruta/Nuevos/Renovaciones/PuntajeDesempeno (gethomesampledatarutas.cs:131-134) */}
        <CCol md={7} lg={8}>
          <CCard className="h-100">
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <span>{`DESEMPEÑO POR RUTA ${mes}`}</span>
              <span className="d-flex align-items-center">
                <BotonInfoChart
                  titulo="Desempeño por ruta"
                  texto={
                    <>
                      <div><strong>Nuevos</strong> / <strong>Renovaciones</strong>: préstamos creados en el mes por tipo.</div>
                      <div className="mt-1"><strong>Puntaje</strong> (eje derecho, en puntos): +12 pts si cumple la meta de nuevos y +12 pts si cumple la de renovados (con bono por renovar); también suman el % de la meta alcanzado y la utilidad recaudada.</div>
                      <div className="mt-1">Sin préstamos en rojo (vencidos o con mora de más de 30 días) suma 15 pts; cada préstamo en rojo RESTA 2 pts.</div>
                    </>
                  }
                />
                <BotonCapturaChart contenedorRef={refDesempeno} nombre="desempeno-por-ruta" />
              </span>
            </CCardHeader>
            <CCardBody className="position-relative" ref={refDesempeno}>
              {desempenoLabels.length === 0 ? (
                <CAlert color="info" className="mb-0">No hay información disponible para este periodo</CAlert>
              ) : (
                <CChartBar
                  style={{ height: '310px' }}
                  data={{
                    labels: desempenoLabels,
                    datasets: [
                      // Colores sincronizados con los KPI (dashboardColors — mismos tokens que WidgetsKpi)
                      { label: 'Nuevos', data: desempenoNuevos, backgroundColor: dashboardColors.success },
                      { label: 'Renovaciones', data: desempenoRenovaciones, backgroundColor: dashboardColors.info },
                      // [RGM] 2026-10-01 (mejora aprobada — doble eje): Puntaje en su eje (puntos).
                      { label: 'Puntaje', data: desempenoPuntaje, backgroundColor: dashboardColors.warning, yAxisID: 'y1' },
                    ],
                  }}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'bottom' },
                      datalabels: { anchor: 'end', align: 'end', font: { size: 10 } },
                      // [RGM] 2026-10-01: tooltip del Puntaje con 2 decimales + " pts".
                      tooltip: {
                        callbacks: {
                          label: (ctx) =>
                            ctx.datasetIndex === 2
                              ? ` Puntaje: ${Number(ctx.parsed.y).toFixed(2)} pts`
                              : ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
                        },
                      },
                    },
                    scales: {
                      // [RGM] 2026-10-03: border = borde del EJE (la línea del
                      // lado de los valores) — sin esto Chart.js lo pinta con
                      // su default (negro) y en dark desaparece. Mismo gris de
                      // la grilla; las líneas de la grilla NO se tocan.
                      x: { grid: { display: false }, border: { display: true, color: 'rgba(128,128,128,0.25)' } },
                      y: { beginAtZero: true, grid: { color: 'rgba(128,128,128,0.25)' }, border: { display: true, color: 'rgba(128,128,128,0.25)' }, ticks: { color: opcionesEjes().scales.y.ticks.color } },
                      // [RGM] 2026-10-01 (mejora aprobada — doble eje): el Puntaje
                      // vive en su propio eje (puntos) para no aplastar las barras.
                      y1: {
                        position: 'right' as const,
                        beginAtZero: true,
                        grid: { drawOnChartArea: false },
                        border: { display: true, color: 'rgba(128,128,128,0.25)' },
                        ticks: { color: opcionesEjes().scales.y.ticks.color, callback: (v: string | number) => `${v} pts` },
                      },
                    },
                  }}
                />
              )}
            </CCardBody>
          </CCard>
        </CCol>

        {/* Cartera — título "Cartera" (home.cs:3690), grilla H001G3 10 filas (home.cs:2627) */}
        <CCol md={5} lg={4}>
          <CCard className="h-100">
            <CCardHeader>Cartera</CCardHeader>
            <CCardBody className="p-0">
              {cargandoCartera ? (
                <div className="text-center p-4"><CSpinner size="sm" /></div>
              ) : panel.carteraRutas.length === 0 ? (
                <CAlert color="info" className="m-3 mb-0">No hay información disponible para este periodo</CAlert>
              ) : (
                <CTable hover small className="ww-table-cartera mb-0">
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Zona</CTableHeaderCell>
                      <CTableHeaderCell>Cobrador</CTableHeaderCell>
                      <CTableHeaderCell className="text-end text-nowrap">Total</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {panel.carteraRutas.map((fila) => (
                      <CTableRow key={`${fila.zonaCodigo}-${fila.cobradorCodigo}`}>
                        <CTableDataCell>{fila.zonaNombre}</CTableDataCell>
                        <CTableDataCell>{fila.cobradorNombreCompleto}</CTableDataCell>
                        <CTableDataCell className="text-end text-nowrap">{fmtTotal(fila.total)}</CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              )}
            </CCardBody>
            <CCardFooter className="d-flex align-items-center">
              {totalPaginas > 1 && (
                <span className="d-flex align-items-center gap-1">
                  <span className="small text-body-secondary me-1">Página {page} de {totalPaginas}</span>
                  <CTooltip content="Primera página" placement="top">
                    <CButton size="sm" color="secondary" variant="outline" disabled={page === 1 || cargandoCartera}
                      onClick={() => setPage(1)}>«</CButton>
                  </CTooltip>
                  <CTooltip content="Página anterior" placement="top">
                    <CButton size="sm" color="secondary" variant="outline" disabled={page === 1 || cargandoCartera}
                      onClick={() => setPage(page - 1)}>‹</CButton>
                  </CTooltip>
                  <CTooltip content="Página siguiente" placement="top">
                    <CButton size="sm" color="secondary" variant="outline" disabled={page === totalPaginas || cargandoCartera}
                      onClick={() => setPage(page + 1)}>›</CButton>
                  </CTooltip>
                  <CTooltip content="Última página" placement="top">
                    <CButton size="sm" color="secondary" variant="outline" disabled={page === totalPaginas || cargandoCartera}
                      onClick={() => setPage(totalPaginas)}>»</CButton>
                  </CTooltip>
                </span>
              )}
              <span className="ms-auto fw-semibold">Total Cartera: {fmtMoneda(panel.totalCartera)}</span>
            </CCardFooter>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-4 mt-0">
        {/* Progreso/Meta mes — título runtime home.cs:2434, chart ColumnLine 450px con valores (home.cs:3664-3669) */}
        <CCol md={7}>
          <CCard className="h-100">
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <span>{`PROGRESO META ${mes} - ESPERADO: ${fmtGx(panel.progreso.esperado)} - PROGRESO: ${fmtGx(panel.progreso.progreso)} (${fmtGx(panel.progreso.porcentaje)}%)`}</span>
              <span className="d-flex align-items-center">
                <BotonInfoChart
                  titulo="Progreso Meta"
                  texto={
                    <>
                      <div><strong>Meta Mes</strong> (riel gris): rendimiento (utilidad) ESPERADO del mes para la ruta.</div>
                      <div className="mt-1"><strong>Progreso</strong> (verde): rendimiento (utilidad) que lleva acumulado la ruta en el mes.</div>
                      <div className="mt-1"><strong>Meta Mínima</strong> (línea roja punteada): el mínimo esperado del mes (meta × % mínimo ÷ % máximo); al superar la línea, el progreso ya pasó la mínima.</div>
                      <div className="mt-1">El (%) junto a la ruta es el cumplimiento del mes: Progreso ÷ Meta Mes.</div>
                    </>
                  }
                />
                <BotonCapturaChart contenedorRef={refProgreso} nombre="progreso-meta" />
              </span>
            </CCardHeader>
            <CCardBody className="h-100 d-flex flex-column position-relative" ref={refProgreso}>
              {metasMes.length === 0 ? (
                <CAlert color="info" className="mb-0">No hay información disponible para este periodo</CAlert>
              ) : (
                <CChart
                  type="bar"
                  className="flex-grow-1 ww-chart-fill"
                  data={{
                    labels: metasMes.map((m) => m.ruta),
                    datasets: [
                      // [RGM] 2026-10-01 (pedido Rafael, fiel a HomeSampleDataMetas GX):
                      // la barra = META MES (riel gris) y el PROGRESO la LLENA —
                      // barras superpuestas (grouped:false), Progreso VERDE. La META
                      // MÍNIMA es una línea punteada ROJA con un PUNTO por ruta: al
                      // superarla se ve que el progreso ya pasó la mínima.
                      { type: 'bar', label: 'Meta Mes', data: metasMes.map((m) => m.meta), backgroundColor: 'rgba(128,128,128,0.25)', grouped: false },
                      { type: 'bar', label: 'Progreso', data: metasMes.map((m) => m.actual), backgroundColor: '#1E9E6A', grouped: false },
                      { type: 'line', label: 'Meta Mínima', data: metasMes.map((m) => m.media), borderColor: '#d64545', borderDash: [6, 4], borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#d64545', pointBorderColor: '#d64545', fill: false },
                    ],
                  }}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'bottom' },
                      // Solo la barra de Progreso muestra su valor (GX ShowValues).
                      datalabels: {
                        display: (ctx: { datasetIndex: number }) => ctx.datasetIndex === 1,
                        anchor: 'end',
                        align: 'end',
                        font: { size: 10 },
                        formatter: (v: number) => fmtGx(v),
                      },
                    },
                    scales: {
                      x: { grid: { display: false }, border: { display: true, color: 'rgba(128,128,128,0.25)' } },
                      y: {
                        beginAtZero: true,
                        suggestedMax: metasMesMax * 1.15,
                        grid: { color: 'rgba(128,128,128,0.25)' },
                        border: { display: true, color: 'rgba(128,128,128,0.25)' },
                        ticks: { color: opcionesEjes().scales.y.ticks.color },
                      },
                    },
                  }}
                />
              )}
            </CCardBody>
          </CCard>
        </CCol>

        {/* Meta Diaria — título "Meta Diaria" (home.cs:3646), chart Bar horizontal 450px (home.cs:3651-3653) */}
        <CCol md={5}>
          <CCard className="h-100">
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <span>Meta Diaria</span>
              <span className="d-flex align-items-center">
                <BotonInfoChart
                  titulo="Meta Diaria"
                  texto={
                    <>
                      <div><strong>Meta Diaria</strong> (riel gris): rendimiento (utilidad) ESPERADO por día en la ruta — meta esperada del mes ÷ días del mes.</div>
                      <div className="mt-1"><strong>Progreso</strong> (verde): rendimiento (utilidad) de hoy en la ruta.</div>
                      <div className="mt-1">El (%) junto a la ruta es el avance del día: Progreso ÷ Meta Diaria.</div>
                    </>
                  }
                />
                <BotonCapturaChart contenedorRef={refMetaDiaria} nombre="meta-diaria" />
              </span>
            </CCardHeader>
            <CCardBody className="h-100 d-flex flex-column position-relative" ref={refMetaDiaria}>
              {metaDiariaLabels.length === 0 ? (
                <CAlert color="info" className="mb-0">No hay información disponible para este periodo</CAlert>
              ) : (
                <CChartBar
                  className="flex-grow-1 ww-chart-fill"
                  data={{
                    labels: metaDiariaLabels,
                    datasets: [
                      // [RGM] 2026-10-01 (pedido Rafael — fiel a HomeSampleDataControl GX):
                      // riel META DIARIA (gris) y PROGRESO (verde, rendimiento del día)
                      // que lo LLENA — barras superpuestas (grouped:false). Sin barra
                      // Media (el sample GX del diario trae únicamente Meta y Actual).
                      { label: 'Meta Diaria', data: metaDiariaMetas, backgroundColor: 'rgba(128,128,128,0.25)', grouped: false },
                      { label: 'Progreso', data: metaDiariaActuales, backgroundColor: '#1E9E6A', grouped: false },
                    ],
                  }}
                  options={{
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'bottom' },
                      // Solo la barra Progreso muestra su valor.
                      datalabels: {
                        display: (ctx: { datasetIndex: number }) => ctx.datasetIndex === 1,
                        anchor: 'end',
                        align: 'end',
                        font: { size: 10 },
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        suggestedMax: metaDiariaMax * 1.15,
                        grid: { color: 'rgba(128,128,128,0.25)' },
                        border: { display: true, color: 'rgba(128,128,128,0.25)' },
                        ticks: { color: opcionesEjes().scales.x.ticks.color },
                      },
                      y: { grid: { display: false }, border: { display: true, color: 'rgba(128,128,128,0.25)' } },
                    },
                  }}
                />
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Componente NUEVO [RGM] aprobado por Rafael (FAB TronIA — diseño propio,
          no es réplica de un proc GX; navega a Mi Cuenta > TronIA) */}
      <TroniaFab />
    </>
  )
}

export default HomePage
